import { usePins } from "components/containers/inspectionDetail/hooks/usePins";
import Peripheral from "libs/peripheral/Peripheral";
import { PeripheralType, PressureValue } from "libs/peripheral/type";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPinDetail } from "redux/pinSlice";
import { RootState } from "redux/store";

const PeripheralContext = createContext<{
  camera(inspectionItemResultId: string): boolean;
  pressure(inspectionItemResultId: string): void;
  onCamera(
    inspectionItemResultId: string,
    callback: (file: File) => void
  ): () => void;
  onPressure(
    inspectionItemResultId: string,
    callback: (data: PressureValue) => void
  ): () => void;
}>({} as any);

export const usePeripheral = () => {
  return useContext(PeripheralContext);
};

const PeripheralProvider: React.FC<
  React.PropsWithChildren<{
    isFetchingContent?: boolean;
  }>
> = ({ children, isFetchingContent }) => {
  const { pinSelected } = useSelector((state: RootState) => state.pin);
  const { pins } = usePins();
  const dispatch = useDispatch();

  const pinId = pinSelected?.id ?? null;

  const identifyInfo = useCallback(
    (inspectionItemResultId: string) => {
      return JSON.stringify({
        id: inspectionItemResultId,
        pinId,
      });
    },
    [pinId]
  );

  const camera = useCallback(
    (inspectionItemResultId: string) => {
      if (!Peripheral.extensions[PeripheralType.CAMERA].isCameraConnected()) {
        return false;
      }

      Peripheral.request(
        PeripheralType.CAMERA,
        identifyInfo(inspectionItemResultId)
      );

      return true;
    },
    [identifyInfo]
  );

  const pressure = useCallback(
    (inspectionItemResultId: string) => {
      return Peripheral.request(
        PeripheralType.PRESSURE,
        identifyInfo(inspectionItemResultId)
      );
    },
    [identifyInfo]
  );

  const onCamera = useCallback(
    (inspectionItemResultId: string, callback: (file: File) => void) => {
      return Peripheral.onMessage(
        PeripheralType.CAMERA,
        identifyInfo(inspectionItemResultId),
        callback
      );
    },
    [identifyInfo]
  );

  const onPressure = useCallback(
    (
      inspectionItemResultId: string,
      callback: (data: PressureValue) => void
    ) => {
      return Peripheral.onMessage(
        PeripheralType.PRESSURE,
        identifyInfo(inspectionItemResultId),
        callback
      );
    },
    [identifyInfo]
  );

  const refPins = useRef(pins);
  refPins.current = pins;

  const currentContentItemId = useRef<string | null>(null);

  useEffect(() => {
    Peripheral.anyMessage = async ({ identify_info }) => {
      const serialized = JSON.parse(identify_info);

      // If the pin is already selected, do not fetch the pin detail
      if (serialized.pinId === pinId) return;

      const index = refPins.current.findIndex((p) => p.id === serialized.pinId);

      // If the pin is not found, do not fetch the pin detail
      if (index === -1) return;

      const pin = refPins.current[index];

      dispatch(fetchPinDetail({ id: pin.id, index }));

      currentContentItemId.current = serialized.id;
    };
  }, [pinId, dispatch]);

  useEffect(() => {
    // After fetching the pin detail, scroll to the content item
    if (!isFetchingContent && currentContentItemId.current && pinSelected?.id) {
      const timeout = setTimeout(() => {
        const element = document.querySelector(
          `[data-id="${currentContentItemId.current}"]`
        );
        element?.scrollIntoView({ behavior: "smooth" });
        currentContentItemId.current = null;
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [isFetchingContent, pinSelected]);

  return (
    <PeripheralContext.Provider
      value={{
        camera,
        pressure,
        onCamera,
        onPressure,
      }}
    >
      {children}
    </PeripheralContext.Provider>
  );
};

export default PeripheralProvider;
